import { z } from 'zod';
import { SKILL_KEYS, type SkillKey } from './progress';

// Courses, as Lagos Life's 🎓 Courses (worked out from its public client code): enrol and pay once, then each
// lesson is a class at the university (two a day at most). After the class the notes are ready; read them and
// pass the lesson's quiz to move on (a failed quiz reopens a few minutes later). Pass every lesson and the
// certificate is yours: the skill grows faster for good, and if the course leads to a job, promotions there come
// sooner. All of it is content the admins edit, the lessons and their questions included.

const id = z.string().regex(/^[a-z0-9_]{2,40}$/);
export const QuizQuestion = z.object({
  q: z.string().trim().min(5).max(200),
  right: z.string().trim().min(1).max(120),
  // the wrong answers shown with it (three is usual)
  wrong: z.array(z.string().trim().min(1).max(120)).min(1).max(5),
});
export type QuizQuestion = z.infer<typeof QuizQuestion>;
export const Lesson = z.object({
  title: z.string().trim().min(3).max(80),
  // the notes, a paragraph each
  notes: z.array(z.string().trim().min(10).max(600)).min(1).max(8),
  questions: z.array(QuizQuestion).min(1).max(30),
});
export type Lesson = z.infer<typeof Lesson>;
export const Course = z.object({
  id,
  label: z.string().trim().min(2).max(60),
  emoji: z.string().trim().min(1).max(16),
  blurb: z.string().trim().min(10).max(240),
  skill: z.enum(SKILL_KEYS as [SkillKey, ...SkillKey[]]),
  // the job (an activity) a certificate helps with; none for a life skill
  career: id.nullable(),
  feePence: z.number().int().min(0).max(100_000_000),
  lessons: z.array(Lesson).min(1).max(12),
  visible: z.boolean(),
});
export type Course = z.infer<typeof Course>;

export const CoursesContent = z.object({
  visible: z.boolean(),
  // classes are held at this place, by doing this activity there
  placeId: id,
  classActivityId: id,
  classesPerDay: z.number().int().min(1).max(10),
  // the quiz: how many questions are asked, how many right answers pass, how many misses end it early, and how
  // long until a failed quiz reopens
  questionsPerQuiz: z.number().int().min(1).max(20),
  passMark: z.number().int().min(1).max(20),
  missesAllowed: z.number().int().min(0).max(20),
  retryMinutes: z.number().int().min(0).max(1440),
  // what a lesson and a certificate are worth: skill XP and wish points; a certificate's lasting boosts
  lessonXp: z.number().int().min(0).max(1000),
  lessonWishPoints: z.number().int().min(0).max(100),
  certificateWishPoints: z.number().int().min(0).max(500),
  certificateXpBoost: z.number().min(0).max(2),
  // a certificate's job gets promotions this much sooner (0.15: 15% fewer shifts for each level)
  certificateCareerBoost: z.number().min(0).max(0.9),
  courses: z.array(Course).min(1).max(60),
});
export type CoursesContent = z.infer<typeof CoursesContent>;

export function coursesProblems(courses: CoursesContent): string[] {
  const problems: string[] = [];
  const ids = new Set(courses.courses.map((course) => course.id));
  if (ids.size !== courses.courses.length) problems.push('Courses: two courses have the same id.');
  if (courses.passMark > courses.questionsPerQuiz)
    problems.push('Courses: the pass mark is more than the questions asked.');
  for (const course of courses.courses)
    course.lessons.forEach((lesson, index) => {
      if (lesson.questions.length < Math.min(courses.passMark, courses.questionsPerQuiz))
        problems.push(`Courses: ${course.label}, lesson ${index + 1} has too few questions to pass.`);
    });
  return problems;
}

// ---------- the rules ----------

// how many questions a lesson's quiz asks, and how many right answers pass it
export function quizSize(courses: CoursesContent, lesson: Lesson) {
  const asked = Math.min(courses.questionsPerQuiz, lesson.questions.length);
  return { asked, pass: Math.min(courses.passMark, asked) };
}
// lessons passed, and the certificate once all are passed
export const certified = (course: Course, done: number) => done >= course.lessons.length;
// the courses a life holds a certificate for
export const certificatesOf = (courses: CoursesContent, done: Record<string, number>) =>
  courses.courses.filter((course) => certified(course, done[course.id] ?? -1));
// a certificate for the job: its promotions come sooner
export const careerEased = (courses: CoursesContent, done: Record<string, number>, jobId: string) =>
  certificatesOf(courses, done).some((course) => course.career === jobId) ? courses.certificateCareerBoost : 0;
// a seeded shuffle (the quiz's questions and their answers in a fixed order for one sitting)
export function shuffled<T>(items: readonly T[], seed: number) {
  const list = [...items];
  let state = seed >>> 0 || 1;
  for (let index = list.length - 1; index > 0; index -= 1) {
    state = Math.imul(state ^ (state >>> 15), 0x2c1b3c6d) >>> 0;
    state = (state ^ (state >>> 12)) >>> 0;
    const pick = state % (index + 1);
    [list[index], list[pick]] = [list[pick]!, list[index]!];
  }
  return list;
}
// one sitting's questions: which of the lesson's questions, and the answers in the order shown
export function quizFor(courses: CoursesContent, lesson: Lesson, seed: number) {
  const { asked } = quizSize(courses, lesson);
  return shuffled(
    lesson.questions.map((_, index) => index),
    seed,
  )
    .slice(0, asked)
    .map((index, position) => {
      const question = lesson.questions[index]!;
      return {
        index,
        q: question.q,
        answers: shuffled([question.right, ...question.wrong], seed + position * 7919),
      };
    });
}

// ---------- over the wire ----------

export const CourseProgressView = z.object({
  id: z.string(),
  enrolled: z.boolean(),
  done: z.number().int(),
  lessons: z.number().int(),
  certified: z.boolean(),
  // a class attended and its notes waiting (the quiz to pass), from when; and when a failed quiz reopens
  notesFrom: z.string().nullable(),
  retryAt: z.string().nullable(),
  // the class is over and its notes can be read; minutes until a failed quiz reopens (0 when open)
  notesReady: z.boolean(),
  retryMinutes: z.number().int(),
});
export type CourseProgressView = z.infer<typeof CourseProgressView>;
export const CoursesReply = z.object({
  courses: z.array(CourseProgressView),
  classesToday: z.number().int(),
  walletPence: z.number().int(),
  // at the university now (classes can be attended)
  atClass: z.boolean(),
  message: z.string().optional(),
});
export type CoursesReply = z.infer<typeof CoursesReply>;
// a quiz sitting: the questions (answers shuffled, the right one not said) and how it is going
export const QuizView = z.object({
  course: z.string(),
  lesson: z.number().int(),
  title: z.string(),
  questions: z.array(z.object({ q: z.string(), answers: z.array(z.string()) })),
  // the next question to answer, right and wrong so far
  at: z.number().int(),
  right: z.number().int(),
  wrong: z.number().int(),
  pass: z.number().int(),
  missesAllowed: z.number().int(),
});
export type QuizView = z.infer<typeof QuizView>;
export const CourseEnrol = z.object({ course: id }).strict();
export type CourseEnrol = z.infer<typeof CourseEnrol>;
export const QuizAnswer = z.object({ answer: z.string().trim().min(1).max(120) }).strict();
export type QuizAnswer = z.infer<typeof QuizAnswer>;
// one answer marked: whether it was right and what was; when the quiz is over, whether it passed
export const QuizAnswerReply = z.object({
  correct: z.boolean(),
  rightAnswer: z.string(),
  quiz: QuizView,
  finished: z.boolean(),
  passed: z.boolean(),
  courses: CoursesReply,
});
export type QuizAnswerReply = z.infer<typeof QuizAnswerReply>;
